import {
  APPLICATION_TYPE,
  CandidateOnlyFilters,
  ProspectOnlyFilters,
  SavedSearch,
  SCORECARD_STATUS,
  SearchFormData,
  TalentSearchQueryParams,
} from '../shared/types';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import FieldObject from '../components/custom_fields/field_object';

function wrapInQuotes(value: string): string {
  value = value.trim();

  if (value.length < 2 || !value.includes(' ')) {
    return value;
  }

  value = value.replace(/"/g, "'");

  return `"${value}"`;
}

function mapEachFieldsToParams(
  field: FieldObject,
  params: TalentSearchQueryParams
) {
  if (field.isValid()) {
    const key = field.queryParamKey();
    const fieldValue = field.queryParamValue();

    if (!fieldValue) {
      return;
    }

    if (key in params && Array.isArray(params[key])) {
      params[key]?.push(fieldValue.toString());
    } else {
      params[key] = [fieldValue.toString()];
    }
  }
}

function presence(arr: string[] | undefined): string[] | undefined {
  return arr?.length ? arr : undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapEachItemToParams(field: Item, customKey: string, params: any) {
  if (field) {
    if (customKey in params && Array.isArray(params[customKey])) {
      params[customKey]?.push(field.value.toString());
    } else {
      params[customKey] = [field.value.toString()];
    }
  }
}

const queryParams = (
  searchFormData: SearchFormData,
  reviewAll = false,
  useCachedFilters = false
): TalentSearchQueryParams => {
  if (searchFormData.application_type !== undefined) {
    const fieldsToExclude =
      searchFormData.application_type === APPLICATION_TYPE.CANDIDATE
        ? ProspectOnlyFilters
        : CandidateOnlyFilters;

    fieldsToExclude.forEach((key) => {
      delete searchFormData[key as keyof SearchFormData];
    });
  }

  const {
    must_have,
    can_have,
    does_not_have,
    hiring_plan_id,
    job_status,
    interview_stage_id,
    stage_status_id,
    referrals_only,
    internal_only,
    exclude_rejected_hiring_plan_id,
    candidate_tags,
    without_candidate_tags,
    scorecard_status,
    custom_fields,
    milestone,
    stages,
    rejection_reasons, // Used in legacy Talent RedisDiscovery page
    exclude_by_rejection_reasons, // Used in legacy Talent Rediscovery page
    include_rejection_reasons, // Used in Talent Matching Rediscovery tab
    exclude_rejection_reasons, // Used in Talent Matching Rediscovery tab
    appliedOnStart, // Used in Talent Matching Rediscovery tab
    appliedOnEnd, // Used in Talent Matching Rediscovery tab
    scorecard_performance,
    no_strong_no_scores,
    exclude_hired,
    exclude_rejected, // Used in Talent Matching Rediscovery tab
    exclude_strong_no_attributes, // Used in Talent Matching Rediscovery tab
    exclude_current_job_people, // Used in Talent Matching Rediscovery tab
    exclude_anonymized,
    last_activity_start,
    last_activity_end,
    schools,
    degrees,
    disciplines,
    application_fields,
    jobs,
    departments,
    offices,
    prospect_pools,
    prospect_pool_stages,
    prospect_owners,
    sources,
    events,
    application_created_at,
    application_created_end,
    location_radius,
    location,
    job_alert_subscribers,
    job_dreamers,
    application_type,
    years_of_experience_min,
    years_of_experience_job_name,
    use_semantic_search,
    scorecard_attribute_filters,
    scorecard_attribute_match,
  } = searchFormData;

  const toIds = (items: Item[]) => items.map((item) => item.value.toString());

  const includeRejectionReasonIds = (): string[] | undefined => {
    if (include_rejection_reasons?.length) {
      return toIds(include_rejection_reasons);
    }
    if (rejection_reasons?.length && !exclude_by_rejection_reasons) {
      return toIds(rejection_reasons);
    }
    return undefined;
  };

  const excludeRejectionReasonIds = (): string[] | undefined => {
    if (exclude_rejection_reasons?.length) {
      return toIds(exclude_rejection_reasons);
    }
    if (rejection_reasons?.length && exclude_by_rejection_reasons) {
      return toIds(rejection_reasons);
    }
    return undefined;
  };

  if (reviewAll || useCachedFilters) {
    return {
      hiring_plan_id: hiring_plan_id?.toString() as string,
      interview_stage_id: interview_stage_id?.toString() as string,
      stage_status_id: stage_status_id?.toString() as string,
      full_text: '1',
      use_cached_filters: useCachedFilters.toString() as 'true' | 'false',
    };
  }

  let search_terms = [...(must_have ?? [])].map(wrapInQuotes).join(' AND ');
  const canHave = [...(can_have ?? [])].map(wrapInQuotes).join(' OR ');
  const doesNotHave = [...(does_not_have ?? [])].map(wrapInQuotes).join(' OR ');

  if (canHave) {
    search_terms = search_terms
      ? `${search_terms} AND (${canHave})`
      : `${canHave}`;
  }

  if (doesNotHave) {
    search_terms = search_terms
      ? `${search_terms} AND NOT (${doesNotHave})`
      : `NOT (${doesNotHave})`;
  }

  const hasScorecardAttributes = scorecard_attribute_filters?.length;

  const params = pickBy(
    {
      hiring_plan_id: hiring_plan_id?.toString(),
      interview_stage_id: interview_stage_id?.toString(),
      job_status: job_status || undefined,
      stage_status_id: stage_status_id?.toString(),
      can_have: can_have?.length ? [...can_have] : undefined,
      must_have: must_have?.length ? [...must_have] : undefined,
      does_not_have: does_not_have?.length ? [...does_not_have] : undefined,
      search_terms: search_terms,
      internal_only: internal_only ? '1' : undefined,
      referrals_only: referrals_only ? '1' : undefined,
      exclude_rejected_hiring_plan_id: presence(
        exclude_rejected_hiring_plan_id?.map((id) => id.toString())
      ),
      no_strong_no_scores: no_strong_no_scores ? '1' : undefined,
      exclude_hired: exclude_hired ? '1' : undefined,
      exclude_rejected: exclude_rejected ? '1' : undefined,
      exclude_strong_no_attributes: exclude_strong_no_attributes
        ? '1'
        : undefined,
      exclude_current_job_people: exclude_current_job_people ? '1' : undefined,
      exclude_anonymized: exclude_anonymized ? '1' : undefined,
      last_activity_start: last_activity_start || undefined,
      last_activity_end: last_activity_end || undefined,
      candidate_tag_id: presence(
        candidate_tags?.map((tag) => tag.value.toString())
      ),
      without_candidate_tag_id: presence(
        without_candidate_tags?.map((tag) => tag.value.toString())
      ),
      scorecard_status:
        scorecard_status !== SCORECARD_STATUS.ALL
          ? scorecard_status
          : undefined,
      reached_milestone_type_id: milestone?.value
        ? [milestone.value]
        : undefined,
      in_stages: stages?.map((stage) => stage.value) || undefined,
      candidate_rejection_reason_id: includeRejectionReasonIds(),
      exclude_candidate_rejection_reason_id: excludeRejectionReasonIds(),
      school_name_id: presence(
        schools?.map((school) => school.value.toString())
      ),
      degree_id: presence(degrees?.map((degree) => degree.value.toString())),
      discipline_id: presence(
        disciplines?.map((discipline) => discipline.value.toString())
      ),
      full_text: '1',
      application_created_at:
        application_created_at || appliedOnStart || undefined,
      application_created_end:
        application_created_end || appliedOnEnd || undefined,
      scorecard_percent_yes_strong_yes:
        scorecard_performance?.value || undefined,
      type: application_type,
      job_alert_subscribers: job_alert_subscribers ? '1' : undefined,
      job_dreamers: job_dreamers ? '1' : undefined,
      years_of_experience_min: years_of_experience_min
        ? years_of_experience_min.toString()
        : undefined,
      years_of_experience_job_name: years_of_experience_job_name,
      use_semantic_search: use_semantic_search ? '1' : '0',
      scorecard_attribute_names: hasScorecardAttributes
        ? scorecard_attribute_filters.map((attr) => attr.value.toString())
        : undefined,
      scorecard_attribute_match: hasScorecardAttributes
        ? scorecard_attribute_match
        : undefined,
    },
    identity
  );

  custom_fields?.forEach((field) => {
    mapEachFieldsToParams(field, params as TalentSearchQueryParams);
  });

  application_fields?.forEach((field) => {
    mapEachFieldsToParams(field, params as TalentSearchQueryParams);
  });

  if (jobs?.length) {
    jobs.forEach((job: Item) => {
      mapEachItemToParams(
        job,
        'hiring_plan_id',
        params as TalentSearchQueryParams
      );
    });
  }

  if (prospect_pools?.length) {
    prospect_pools.forEach((pool: Item) => {
      mapEachItemToParams(
        pool,
        'prospect_pool_id',
        params as TalentSearchQueryParams
      );
    });
  }

  if (prospect_pool_stages?.length) {
    prospect_pool_stages.forEach((stage: Item) => {
      mapEachItemToParams(
        stage,
        'in_prospect_stages',
        params as TalentSearchQueryParams
      );
    });
  }

  if (departments?.length) {
    departments.forEach((department: Item) => {
      mapEachItemToParams(
        department,
        'department_id',
        params as TalentSearchQueryParams
      );
    });
  }

  if (prospect_owners?.length) {
    prospect_owners.forEach((owner: Item) => {
      mapEachItemToParams(
        owner,
        'prospect_owner_id',
        params as TalentSearchQueryParams
      );
    });
  }

  if (offices?.length) {
    offices.forEach((office: Item) => {
      mapEachItemToParams(
        office,
        'office_id',
        params as TalentSearchQueryParams
      );
    });
  }

  if (sources?.length) {
    sources.forEach((source: Item) => {
      mapEachItemToParams(
        source,
        'source_id',
        params as TalentSearchQueryParams
      );
    });
  }

  if (events?.length) {
    events.forEach((event: Item) => {
      mapEachItemToParams(
        event,
        'prospecting_event_id',
        params as TalentSearchQueryParams
      );
    });
  }

  const result = params as unknown as TalentSearchQueryParams;

  if (location_radius && location) {
    result.geolocation_radius = location_radius;
    result.geolocation = location;
  }

  return result;
};

export function toQueryString(params: TalentSearchQueryParams): string {
  const searchParams = new URLSearchParams();

  for (const paramKey in params) {
    if (params.hasOwnProperty(paramKey)) {
      const key = paramKey as keyof TalentSearchQueryParams;
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach((element) => {
          searchParams.append(`${key}[]`, element);
        });
      } else if (value && typeof value === 'object') {
        Object.entries(value).forEach(([subKey, subValue]) => {
          searchParams.append(`${key}[${subKey}]`, subValue as string);
        });
      } else {
        searchParams.append(key, value as string);
      }
    }
  }

  return searchParams.toString();
}

export function toSavedSearchQueryString(
  params: TalentSearchQueryParams
): string {
  const searchParams = new URLSearchParams();

  for (const paramKey in params) {
    if (params.hasOwnProperty(paramKey)) {
      const key = paramKey as keyof TalentSearchQueryParams;
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach((element) => {
          if (typeof element === 'object') {
            const serializedElement = JSON.stringify(element);
            searchParams.append(`${key}[]`, serializedElement);
          } else {
            searchParams.append(`${key}[]`, element);
          }
        });
      } else if (value && typeof value === 'object') {
        Object.entries(value).forEach(([subKey, subValue]) => {
          searchParams.append(`${key}[${subKey}]`, subValue as string);
        });
      } else {
        searchParams.append(key, value as string);
      }
    }
  }

  return searchParams.toString();
}

export function extractFormFieldsFromSavedSearch(
  params: SavedSearch,
  userDateFormat: string
): SavedSearch {
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      const paramsValue = params[key];
      if (Array.isArray(paramsValue)) {
        params[key] = paramsValue.map((value: string) => {
          if (['custom_fields', 'application_fields'].includes(key)) {
            return new FieldObject(userDateFormat, JSON.parse(value));
          } else if (
            key === 'must_have' ||
            key === 'can_have' ||
            typeof value !== 'string'
          ) {
            return value;
          } else {
            return JSON.parse(value);
          }
        });
      } else if (typeof paramsValue === 'object' && paramsValue !== null) {
        params[key] = paramsValue;
      } else if (paramsValue === 'null') {
        params[key] = null;
      } else if (paramsValue === 'true') {
        params[key] = true;
      } else if (paramsValue === 'false') {
        params[key] = false;
      } else {
        params[key] = params[key];
      }
    }
  }

  return params;
}

export default queryParams;
